/** The orders view's reducers: the order tree, what can be cancelled, and the unresolved exposure (U041, U042). */
import type { OrderRow, ReconciliationRow } from "../../contracts/account.ts";
import { formatQuantity } from "../../app/format.ts";

export interface OrderNode {
  readonly row: OrderRow;
  readonly children: OrderNode[];
  /** The parent id names no order in the page: shown as a root and said so. */
  readonly orphan: boolean;
}

/** The parent and child tree: roots first in the given order, children under their parent. */
export function buildTree(rows: readonly OrderRow[]): OrderNode[] {
  const nodes = new Map<string, OrderNode>(
    rows.map((row) => [row.orderId, { row, children: [], orphan: false }]),
  );
  const parentOf = (node: OrderNode): OrderNode | undefined =>
    node.row.parentOrderId === null ? undefined : nodes.get(node.row.parentOrderId);
  /** Whether attaching `node` under `parent` would make a loop (a corrupt tree is shown flat, not forever). */
  const loops = (node: OrderNode, parent: OrderNode): boolean => {
    let cursor: OrderNode | undefined = parent;
    for (let step = 0; cursor !== undefined && step <= nodes.size; step += 1) {
      if (cursor === node) {
        return true;
      }
      cursor = parentOf(cursor);
    }
    return false;
  };
  const roots: OrderNode[] = [];
  for (const node of nodes.values()) {
    const parent = parentOf(node);
    if (parent === undefined) {
      roots.push(node.row.parentOrderId === null ? node : { ...node, orphan: true });
    } else if (loops(node, parent)) {
      roots.push({ ...node, orphan: true });
    } else {
      parent.children.push(node);
    }
  }
  return roots;
}

const CANCELLABLE = new Set([
  "SUBMIT_PENDING",
  "ACKNOWLEDGED",
  "WORKING",
  "PARTIALLY_FILLED",
  "REPLACE_PENDING",
]);

/** Whether a cancel request makes sense: the order is live at the broker or on its way. */
export function isCancellable(row: OrderRow): boolean {
  return CANCELLABLE.has(row.state);
}

/** Fill and residual in words; an unknown residual is `unknown`, never zero (UX04). */
export function quantityText(row: OrderRow): string {
  const residual = row.residualQuantity === null ? "unknown" : formatQuantity(row.residualQuantity);
  return `${formatQuantity(row.filledQuantity)} of ${formatQuantity(row.quantity)} filled; residual ${residual}`;
}

export interface Unresolved {
  readonly orderId: string;
  readonly instrument: string;
  readonly reason: string;
}

const PROTECTING = new Set(["ACKNOWLEDGED", "WORKING", "PARTIALLY_FILLED", "FILLED"]);

/** UNKNOWN outcomes, partial fills, failed protection and filled quantity without a live stop (U042). */
export function unresolvedExposure(rows: readonly OrderRow[]): Unresolved[] {
  const found: Unresolved[] = [];
  for (const row of rows) {
    const mark = (reason: string): void => {
      found.push({ orderId: row.orderId, instrument: row.instrument, reason });
    };
    if (row.state === "UNKNOWN") {
      mark("Outcome UNKNOWN: the broker has not confirmed it, so it is neither filled nor closed.");
    }
    if (row.state === "PARTIALLY_FILLED") {
      mark(`Partially filled (${quantityText(row)}): the residual is still open.`);
    }
    if (
      row.role === "PROTECTIVE_STOP" &&
      (row.state === "REJECTED" || row.state === "CANCELLED" || row.state === "UNKNOWN")
    ) {
      mark(`Protection failed: the protective stop is ${row.state.toLowerCase()}.`);
    }
    if (row.role === "ENTRY" && row.filledQuantity > 0) {
      const stops = rows.filter(
        (other) => other.parentOrderId === row.orderId && other.role === "PROTECTIVE_STOP",
      );
      if (!stops.some((stop) => PROTECTING.has(stop.state))) {
        mark("Filled quantity has no live protective stop.");
      }
    }
  }
  return found;
}

/** The differences still open: not resolved (U044). */
export function openDifferences(rows: readonly ReconciliationRow[]): readonly ReconciliationRow[] {
  return rows.filter((row) => row.resolution !== "RESOLVED");
}

/** A quantity that may be absent in words: `none` when the books hold nothing, never zero. */
export function bookQuantity(quantity: number | null): string {
  return quantity === null ? "none" : formatQuantity(quantity);
}
